"use client";
import React, { useEffect, useState, useMemo } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";
import { useAuth } from "../context/AuthContext";
import { Search, MessageSquare, AlertTriangle, CheckCircle2, Clock, FileText, ExternalLink, Eye, X } from "lucide-react";
import Pagination, { usePagination } from "../shared/Pagination";

const PRIORITY_STYLE = {
  high:   { pill: "bg-red-100 text-red-600",    dot: "bg-red-500"    },
  medium: { pill: "bg-amber-100 text-amber-600", dot: "bg-amber-400"  },
  low:    { pill: "bg-teal-100 text-teal-600",   dot: "bg-teal-500"   },
};
const getPriority = p => PRIORITY_STYLE[(p || "").toLowerCase()] || { pill: "bg-gray-100 text-gray-500", dot: "bg-gray-400" };

// Simple single-line clamp for table/card previews — no inline expand logic needed
// anymore since full text now lives in the details modal.
function ClampedText({ text, lines = 2, className = "" }) {
  if (!text) return <span className="text-gray-300 text-xs">—</span>;
  const style = lines === 1
    ? { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
    : { display: "-webkit-box", WebkitLineClamp: lines, WebkitBoxOrient: "vertical", overflow: "hidden" };
  return <div className={className} style={style}>{text}</div>;
}
function ReportDetailsModal({ report, onClose }) {
  if (!report) return null;
  const ps = getPriority(report.priority_level);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg
                   max-h-[90vh] sm:max-h-[85vh] overflow-y-auto
                   animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle - mobile only */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>

        <div className="flex items-start justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div className="min-w-0 pr-3">
            <p className="text-xs text-gray-400 font-medium">Report #{report.id}</p>
            <h2 className="text-sm sm:text-base font-semibold text-gray-800 break-words">{report.title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 sm:px-5 py-4 space-y-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full w-fit ${ps.pill}`}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ps.dot}`} />
              {report.priority_level || "—"}
            </span>
            <span className="text-[11px] text-gray-400">{report.create_audit_time}</span>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-400 mb-1">Subject</p>
            <p className="text-sm text-gray-700 break-words">{report.subject || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-400 mb-1">Email</p>
            <p className="text-sm text-gray-700 break-words">{report.email || "—"}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-400 mb-1">Description</p>
            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed">
              {report.description || "—"}
            </p>
          </div>

        {report.attachment && (
  <div>
    <p className="text-xs font-medium text-gray-400 mb-1">Attachment</p>
    <a href={`${CDN_URL}/${report.attachment}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-teal-600 hover:text-teal-700 font-medium break-all">
      <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" /> View Document
    </a>
  </div>
)}
        </div>
      </div>
    </div>
  );
}

export default function ReportsTable() {
  const { token } = useAuth();
  const [reports,  setReports]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);
  const [search,   setSearch]   = useState("");
  const [priority, setPriority] = useState("all");
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    fetch(`${BASE_URL}/reports`, { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error("Failed to fetch reports"); return r.json(); })
      .then(j => setReports(j.data ?? []))
      .catch(e => setError(e.message || "Something went wrong"))
      .finally(() => setLoading(false));
  }, [token]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return reports.filter(r => {
      const matchSearch = !q ||
        String(r.id).includes(q) ||
        (r.title || "").toLowerCase().includes(q) ||
        (r.subject || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.description || "").toLowerCase().includes(q);
      const matchPriority = priority === "all" || (r.priority_level || "").toLowerCase() === priority;
      return matchSearch && matchPriority;
    });
  }, [reports, search, priority]);

  const { page, pageSize, setPage, setPageSize, totalPages, totalItems, paginated } = usePagination(filtered, 10);

  // KPIs
  const highCount   = reports.filter(r => (r.priority_level||"").toLowerCase() === "high").length;
  const mediumCount = reports.filter(r => (r.priority_level||"").toLowerCase() === "medium").length;
  const lowCount    = reports.filter(r => (r.priority_level||"").toLowerCase() === "low").length;

  if (loading) return <div className="p-6 text-sm text-gray-400 text-center">Loading reports…</div>;
  if (error)   return <div className="p-6 text-sm text-red-500 text-center">{error}</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label:"Total Reports", value:reports.length,  accent:"border-l-teal-500",  color:"text-teal-700",  bg:"bg-teal-50",  icon:MessageSquare },
            { label:"High Priority", value:highCount,        accent:"border-l-red-400",   color:"text-red-500",   bg:"bg-red-50",   icon:AlertTriangle },
            { label:"Medium",        value:mediumCount,      accent:"border-l-amber-400", color:"text-amber-600", bg:"bg-amber-50", icon:Clock         },
            { label:"Low Priority",  value:lowCount,         accent:"border-l-green-500", color:"text-green-600", bg:"bg-green-50", icon:CheckCircle2  },
          ].map(({ label, value, accent, color, bg, icon: Icon }) => (
            <div key={label} className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-4 flex items-center gap-3 shadow-sm`}>
              <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-400 leading-tight">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-3 bg-white border border-gray-200 rounded-xl p-3 shadow-sm items-center">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Search by ID, subject, email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition"
            />
          </div>
          <select
            value={priority}
            onChange={e => setPriority(e.target.value)}
            className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[140px]"
          >
            <option value="all">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <span className="text-xs text-gray-400 px-1">
            {filtered.length} of {reports.length} records
          </span>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-3">
          {paginated.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 py-14 text-center shadow-sm">
              <FileText className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No reports found</p>
            </div>
          ) : paginated.map(r => {
            const ps = getPriority(r.priority_level);
            return (
              <div key={r.id} className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <h2 className="font-semibold text-gray-800 text-sm">{r.id} · {r.title}</h2>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${ps.pill}`}>
                    {r.priority_level}
                  </span>
                </div>
                <p className="text-xs text-gray-600"><span className="font-medium">Subject:</span> {r.subject}</p>
                <p className="text-xs text-gray-600"><span className="font-medium">Email:</span> {r.email}</p>
                {r.description && (
                  <ClampedText text={r.description} lines={2} className="text-xs text-gray-500" />
                )}
                <div className="flex items-center justify-between pt-1">
                  <p className="text-[10px] text-gray-400">{r.create_audit_time}</p>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedReport(r)}
                      className="flex items-center gap-1 text-xs text-teal-600 hover:underline font-medium"
                    >
                      <Eye className="w-3 h-3" /> View Details
                    </button>
                    {r.attachment && (
                      <a href={`${CDN_URL}/${r.attachment}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-teal-600 hover:underline">
                        <ExternalLink className="w-3 h-3" /> Doc
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-teal-700 text-white">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Id</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Type</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Subject</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Priority</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Description</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider hidden lg:table-cell">Email</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Attachment</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Created</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-400 text-sm">
                    No reports found.
                  </td>
                </tr>
              ) : paginated.map((r, idx) => {
                const ps = getPriority(r.priority_level);
                return (
                  <tr key={r.id} className={`transition-colors hover:bg-teal-50/30 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50/40"}`}>
                    <td className="px-4 py-3 text-xs text-gray-400 font-medium">{r.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">{r.title}</td>
                    <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{r.subject}</td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full w-fit ${ps.pill}`}>
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${ps.dot}`} />
                        {r.priority_level || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-500 max-w-[220px]">
                      <ClampedText text={r.description} lines={1} />
                    </td>
                    <td className="px-4 py-3 hidden lg:table-cell text-gray-500 text-xs">{r.email}</td>
                    <td className="px-4 py-3">
                      {r.attachment ? (
                        <a href={`${CDN_URL}/${r.attachment}`} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-xs font-medium">
                          <ExternalLink className="w-3 h-3" /> Document
                        </a>
                      ) : (
                        <span className="text-gray-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{r.create_audit_time}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedReport(r)}
                        className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-xs font-medium"
                      >
                        <Eye className="w-3.5 h-3.5" /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 border-t border-gray-100">
            <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems}
              pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </div>
        </div>
      </div>

      <ReportDetailsModal report={selectedReport} onClose={() => setSelectedReport(null)} />
    </div>
  );
}